import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import {
  WalletService,
  buildTransaction,
  saveWalletConfig,
  saveWalletItems,
  saveWalletPills,
  saveWalletFilters,
  saveWalletPagination,
  saveVoucher
} from '../normalizer'
import { ToastService } from '../../Gamification2026Template/shared/Toast'

class WalletContainer extends Component {
  slugs = () => ({
    clientSlug: this.props.user.client.slug,
    campaignSlug: this.props.campaign.slug
  })

  getWalletConfig = () => {
    this.props.startTransaction(() =>
      WalletService.getWalletConfig(this.slugs())
        .then((response) => this.props.saveWalletConfig(response.data))
        .catch(() => null)
    )
  }

  // The categories endpoint wraps the array under a key named after the
  // endpoint path (e.g. "user/vouchers/categories") rather than a fixed key.
  // Normalise to a flat array so WalletPills always receives an array.
  extractPills = (data) => {
    if (Array.isArray(data)) return data
    if (!data || typeof data !== 'object') return []
    if (Array.isArray(data.categories)) return data.categories
    if (Array.isArray(data.pills)) return data.pills
    if (Array.isArray(data['user/vouchers/categories'])) return data['user/vouchers/categories']
    // Fallback: pick the first array value found on the object.
    const arrayValue = Object.keys(data).map((key) => data[key]).find(Array.isArray)
    return arrayValue || []
  }

  getWalletPills = (filters) => {
    const activeFilters = filters !== undefined ? filters : this.props.walletFilters
    const archiveParam = activeFilters && activeFilters.toggle ? true : undefined
    this.props.startTransaction(() =>
      WalletService.getWalletPills({ archive: archiveParam })
        .then((response) => this.props.saveWalletPills(this.extractPills(response.data)))
        .catch(() => null)
    )
  }

  // The vouchers endpoint wraps the array under a key named after the endpoint
  // path (e.g. "user/vouchers"). Normalise to a flat array the same way as pills.
  extractItems = (data) => {
    if (Array.isArray(data)) return data
    if (!data || typeof data !== 'object') return []
    if (Array.isArray(data.items)) return data.items
    if (Array.isArray(data.vouchers)) return data.vouchers
    if (Array.isArray(data['user/vouchers'])) return data['user/vouchers']
    // Fallback: pick the first array value found on the object.
    var arrayValue = Object.keys(data).map(function(key) { return data[key] }).find(Array.isArray)
    return arrayValue || []
  }

  // Fetch the wallet items. `overrides` lets callers pass the just-computed
  // filters/page explicitly instead of relying on Redux props, which have not
  // yet re-propagated synchronously after a dispatch (avoids the stale "one
  // step behind" request).
  getWalletItems = (overrides) => {
    const opts = overrides || {}
    const payload = {
      filters: opts.filters !== undefined ? opts.filters : this.props.walletFilters,
      page: opts.page !== undefined ? opts.page : this.props.walletPagination.currentPage
    }
    this.props.startTransaction(() =>
      WalletService.getWalletItems(payload)
        .then((response) => {
          const data = response.data
          this.props.saveWalletItems(this.extractItems(data))
          if (data.pagination) {
            this.props.saveWalletPagination({
              currentPage: data.pagination.current_page,
              totalPages: data.pagination.total_pages
            })
          }
        })
        .catch(() => ToastService.error('Erro ao carregar a carteira'))
    )
  }

  handleFilterChange = (partialFilters) => {
    // When the archive toggle changes, the previously selected category may no
    // longer be valid/visible in the new archive state, so reset the type.
    const appliedPartial = 'toggle' in partialFilters
      ? { ...partialFilters, type: '' }
      : partialFilters
    // Compute the next filter state locally (mirrors the reducer's merge) so
    // the fetch uses the newest values without waiting for props to update.
    const nextFilters = { ...this.props.walletFilters, ...appliedPartial }
    this.props.saveWalletFilters(appliedPartial)
    this.props.saveWalletPagination({
      currentPage: 1,
      totalPages: this.props.walletPagination.totalPages
    })
    // When the archive toggle changes, re-fetch categories so only the ones
    // relevant to the current archive state are shown as filter pills.
    if ('toggle' in partialFilters) {
      this.getWalletPills(nextFilters)
    }
    this.getWalletItems({ filters: nextFilters, page: 1 })
  }

  handlePillChange = (type) => {
    this.handleFilterChange({ type: type })
  }

  handlePageChange = (data) => {
    const nextPage = data.selected + 1
    this.props.saveWalletPagination({
      currentPage: nextPage,
      totalPages: this.props.walletPagination.totalPages
    })
    this.getWalletItems({ page: nextPage })
  }

  handleViewDetails = (item) => {
    // Pre-populate Redux with the row data for an instant paint; the voucher
    // detail page's own container immediately re-fetches the full record
    // from GET /api/users/vouchers/:id and overwrites this with fresh data.
    this.props.saveVoucher(item)
    this.props.history.push(`/voucher/${item.id}`)
  }

  componentDidMount() {
    this.getWalletConfig()
    this.getWalletPills()
    this.getWalletItems()
  }

  render() {
    const { component: Component, ...rest } = this.props
    return (
      <Component
        {...rest}
        onFilterChange={this.handleFilterChange}
        onPillChange={this.handlePillChange}
        onPageChange={this.handlePageChange}
        onViewDetails={this.handleViewDetails}
      />
    )
  }
}

const mapStateToProps = (state) => ({
  campaign: state.campaignReducer.campaign,
  user: state.userReducer.user,
  walletConfig: state.walletReducer.config,
  walletItems: state.walletReducer.items,
  walletPills: state.walletReducer.pills,
  walletFilters: state.walletReducer.filters,
  walletPagination: state.walletReducer.pagination
})

const mapDispatchToProps = (dispatch) => ({
  startTransaction: buildTransaction(dispatch),
  saveWalletConfig: (config) => dispatch(saveWalletConfig(config)),
  saveWalletItems: (items) => dispatch(saveWalletItems(items)),
  saveWalletPills: (pills) => dispatch(saveWalletPills(pills)),
  saveWalletFilters: (filters) => dispatch(saveWalletFilters(filters)),
  saveWalletPagination: (pagination) => dispatch(saveWalletPagination(pagination)),
  saveVoucher: (voucher) => dispatch(saveVoucher(voucher))
})

const Container = withRouter(connect(mapStateToProps, mapDispatchToProps)(WalletContainer))

export function withWalletContainer(component) {
  return (props) => (
    <Container
      component={component}
      {...props}
    />
  )
}

export default Container
